import { LitElement, css, html, nothing } from 'lit';
import { api } from '../core/api.js';
import { sessionStore } from '../core/auth.js';
import { navigate } from '../core/router.js';
import { play } from '../core/sound.js';
import { StoreController } from '../core/store.js';
import { notify } from '../core/ui.js';
import './qz-invite-card.js';

/**
 * Co ile pytamy o zaproszenia.
 *
 * Kilkanaście sekund opóźnienia przy „ktoś chce z tobą zagrać" nikomu nie
 * przeszkadza, a to samo żądanie odnotowuje obecność — jedno odpytywanie
 * zamiast dwóch. Gniazdo WebSocket byłoby tu bez sensu: zaproszenie przychodzi
 * wtedy, gdy gracz **nie jest** w pojedynku.
 */
const POLL_MS = 15000;

/**
 * Nasłuch zaproszeń do pojedynku. Jedna instancja w powłoce aplikacji.
 *
 * Sam niczego nie rysuje: wygląd należy do `qz-invite-card`, która dostaje
 * stąd zaproszenie i pozostały czas, a oddaje decyzję gracza. Tutaj zostaje
 * odpytywanie, zegar, dźwięk i wejście do pojedynku.
 */
export class QzInviteWatch extends LitElement {
  static properties = {
    _incoming: { state: true },
    _busy: { state: true },
  };

  static styles = css`
    :host {
      display: contents;
    }
  `;

  #session = new StoreController(this, sessionStore);
  #timer;
  #tick;
  /** Identyfikatory, o których już zabrzmiał sygnał — żeby nie dzwonił co cykl. */
  #announced = new Set();
  /** Zaproszenia własne, po których już weszliśmy do pojedynku. */
  #handled = new Set();
  /** Konto, dla którego trwa nasłuch — zmiana oznacza logowanie albo wylogowanie. */
  #watching = null;

  constructor() {
    super();
    this._incoming = null;
    this._busy = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#timer = setInterval(() => this.#poll(), POLL_MS);
    this.#tick = setInterval(() => this.requestUpdate(), 1000);
  }

  /**
   * Pierwsze odpytanie idzie dopiero wtedy, gdy wiadomo, kim jesteśmy.
   *
   * Przy wejściu na stronę sesja jest jeszcze wczytywana, więc odpytanie
   * z `connectedCallback` odpadałoby jako „gość" i wszystko — zaproszenia
   * i obecność — ruszałoby z piętnastosekundowym opóźnieniem.
   */
  updated() {
    const userId = this.#session.value.user?.id ?? null;
    if (userId === this.#watching) return;

    this.#watching = userId;
    this._incoming = null;
    if (userId) this.#poll();
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    clearInterval(this.#tick);
    super.disconnectedCallback();
  }

  async #poll() {
    // Odpytywanie ma sens tylko dla zalogowanych; ono też odnotowuje obecność,
    // więc gość niepotrzebnie dobijałby się o 401 co kilkanaście sekund.
    if (!this.#session.value.user) {
      this._incoming = null;
      return;
    }

    try {
      const response = await api.invitations.list();
      const items = response.data;

      // Przyjęte zaproszenie własne = przeciwnik już czeka w pojedynku.
      //
      // Przyjęte zaproszenie zostaje na liście przez minutę, żeby zapraszający
      // zdążył je zobaczyć — bez zapamiętania, które już obsłużyliśmy, kolejne
      // odpytania wrzucałyby go do pojedynku co kilkanaście sekund, zrywając
      // po drodze połączenie z rozgrywką.
      const accepted = items.find(
        (item) =>
          item.direction === 'outgoing' &&
          item.status === 'accepted' &&
          item.duelId &&
          !this.#handled.has(item.id),
      );
      if (accepted) {
        this.#handled.add(accepted.id);
        notify(`${accepted.person.name} przyjął zaproszenie.`, { kind: 'success' });
        navigate(`/pojedynek/${accepted.duelId}`);
        return;
      }

      const incoming = items.find((item) => item.direction === 'incoming') ?? null;
      if (incoming && !this.#announced.has(incoming.id)) {
        this.#announced.add(incoming.id);
        play('tick');
      }
      this._incoming = incoming;
    } catch {
      // Zerwana sieć nie może wysypać powłoki aplikacji — spróbujemy za chwilę.
    }
  }

  async #accept() {
    if (this._busy) return;
    this._busy = true;

    try {
      const response = await api.invitations.accept(this._incoming.id);
      this._incoming = null;
      navigate(`/pojedynek/${response.data.duelId}`);
    } catch (err) {
      notify(err.message, { kind: 'error' });
      this._incoming = null;
    } finally {
      this._busy = false;
    }
  }

  async #decline() {
    const invite = this._incoming;
    this._incoming = null;

    try {
      await api.invitations.decline(invite.id);
    } catch {
      // Odrzucone zaproszenie i tak wygaśnie samo — nie ma o czym mówić.
    }
  }

  render() {
    const invite = this._incoming;
    if (!invite) return nothing;

    const secondsLeft = Math.max(0, Math.round((invite.expiresAt - Date.now()) / 1000));
    if (secondsLeft === 0) {
      // Wygasło w trakcie patrzenia — znika bez czekania na kolejne odpytanie.
      queueMicrotask(() => {
        if (this._incoming?.id === invite.id) this._incoming = null;
      });
      return nothing;
    }

    return html`
      <qz-invite-card
        .invite=${invite}
        .secondsLeft=${secondsLeft}
        ?busy=${this._busy}
        @qz-accept=${this.#accept}
        @qz-decline=${this.#decline}
      ></qz-invite-card>
    `;
  }
}

customElements.define('qz-invite-watch', QzInviteWatch);
